import React from 'react'
import { ConfirmationModalGenericProps } from '../MoveStorageItemModal/useConfirmationModal'
import { Modal, Button, ModalInjectedProps } from '@sketch/components'
import {
  StyledSeparator,
  Callout,
} from './ConfirmStorageItemTransferModal.styles'
import {
  ProjectFragment,
  StorageItemFragment,
  WorkspaceMinimalFragment,
  PublicWorkspaceFragment,
  ProjectInSidebarAndHeaderFragment,
  useGetProjectInSidebarQuery,
} from '@sketch/gql-types'
import { StorageItemAccessList } from '../../components/StorageItemAccessList'

type Project = Pick<
  ProjectFragment,
  | 'identifier'
  | 'name'
  | 'workspaceAccessLevel'
  | 'projectType'
  | 'topLevelProject'
>

interface ConfirmStorageItemTransferModalProps
  extends ConfirmationModalGenericProps {
  item: StorageItemFragment
  destination: {
    project: Project | null
    workspace: WorkspaceMinimalFragment
  }
  currentLocation: {
    project: Project | null
    workspace: WorkspaceMinimalFragment | PublicWorkspaceFragment
  }
}

interface DescriptionProps
  extends Pick<
    ConfirmStorageItemTransferModalProps,
    'currentLocation' | 'destination' | 'item'
  > {
  isTheSameWorkspace: boolean
}
const Description = (props: DescriptionProps) => {
  const { currentLocation, destination, item, isTheSameWorkspace } = props
  const currentTopLevelProject =
    currentLocation.project?.topLevelProject || currentLocation.project || null
  const destinationTopLevelProject =
    destination.project?.topLevelProject || destination.project || null

  const itemType = item.__typename === 'Project' ? 'folder' : 'document'

  const isDifferentTopLevelProject =
    currentTopLevelProject?.identifier !==
    destinationTopLevelProject?.identifier

  const isMovingToNestedProject = !!destination.project?.topLevelProject
  const potentialPermissionLossWarning = `Some people may lose access to this ${itemType}. You may want to review its permissions after moving.`

  if (isTheSameWorkspace && isDifferentTopLevelProject) {
    if (isMovingToNestedProject) {
      return (
        <div>
          <b>{item.name}</b> will move into <b>{destination.project?.name}</b>{' '}
          and use the same permissions as{' '}
          <b>{destinationTopLevelProject?.name}</b>.{' '}
          {potentialPermissionLossWarning}
        </div>
      )
    } else {
      return (
        <div>
          <b>{item.name}</b> will move into{' '}
          <b>{destination.project?.name || destination.workspace.name}</b>.{' '}
          {potentialPermissionLossWarning}
        </div>
      )
    }
  }

  return (
    <>
      <div>
        The {itemType} <b>{item.name}</b> and all its contents will move to{' '}
        {destination.project ? (
          <>
            <b>{destination.project.name}</b> of the{' '}
          </>
        ) : null}{' '}
        <b>{destination.workspace.name}</b>. {potentialPermissionLossWarning}
      </div>

      <Callout>
        Once this {itemType} has moved to <b>{destination.workspace.name}</b>,
        only an Admin of that Workspace will be able to move it again.
      </Callout>
    </>
  )
}

export const ConfirmStorageItemTransferModal = (
  props: ConfirmStorageItemTransferModalProps
) => {
  const {
    onCancel,
    onConfirm,
    hideModal,
    loading,
    currentLocation,
    destination,
    item,
  } = props

  const isTheSameWorkspace =
    currentLocation?.workspace?.identifier === destination.workspace.identifier

  const itemType = item.__typename === 'Project' ? 'folder' : 'document'

  return (
    <Modal onCancel={onCancel}>
      <Modal.Header>Move {itemType}?</Modal.Header>

      <Modal.Body>
        <Description
          item={item}
          currentLocation={currentLocation}
          destination={destination}
          isTheSameWorkspace={isTheSameWorkspace}
        />
        <StyledSeparator />
        <StorageItemAccessList
          destination={destination}
          item={item}
          hideSharePermissions={!isTheSameWorkspace}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onCancel}>Go Back</Button>
        <Button
          variant="primary"
          loading={loading}
          onClick={() => onConfirm(hideModal)}
        >
          Move
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

interface ConfirmStorageItemTransferModalDraggedProps
  extends Pick<
      ConfirmStorageItemTransferModalProps,
      'item' | 'onConfirm' | 'loading'
    >,
    ModalInjectedProps {
  workspace: WorkspaceMinimalFragment
  destinationProject: ProjectInSidebarAndHeaderFragment
}

export const ConfirmStorageItemTransferModalDragged = (
  props: ConfirmStorageItemTransferModalDraggedProps
) => {
  const { item, destinationProject, workspace, loading, ...rest } = props

  const currentProjectId = (
    item.__typename === 'Project' ? item.topLevelProject : item.project
  )?.identifier

  // we are expecting to receive this project from a cache
  const currentProjectQuery = useGetProjectInSidebarQuery({
    variables: { shortId: currentProjectId || '' },
    skip: !currentProjectId,
  })

  return (
    <ConfirmStorageItemTransferModal
      {...rest}
      loading={loading || currentProjectQuery.loading}
      onCancel={props.hideModal}
      item={item}
      destination={{
        // We are allowing to drag&drop storage items within the same workspace
        workspace,
        project: destinationProject,
      }}
      currentLocation={{
        workspace,
        project: currentProjectQuery.data?.project || null,
      }}
    />
  )
}
